import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getQuoteV2 } from '../api/quote';
import { CLIENT_INELIGIBLE_ERRORS } from '../constants';
import { useCustomerInfo } from '../context/customerInfo';
import { useFeatures } from '@kmx/io-core/context/FeaturesContext';
import { useFormContext } from '../context/formContext';
import { useOfferContext } from '../context/offerContext';
import { useVehicleInfo, vehicleInfoInitialState } from '../context/vehicleInfo';
import useDecodeVehicle from '../hooks/events/useDecodeVehicle';
import useRequestOffer from '../hooks/events/useRequestOffer';
import useStartQuote from '../hooks/events/useStartQuote';
import useTriggerIneligibleEvent from '../hooks/events/useTriggerIneligibleEvent';
import { IcoStartingMethod } from '../types/IFormMetadata';
import { IOffer } from '../types/IOfferResponse';
import { IQuote } from '../types/IQuote';
import { IVehicleInfo } from '../types/IVehicleInfo';
import { IVinEligibility } from '../types/IVinEligibility';
import { IVinMatchingStylesEligibility } from '../types/IVinMatchingStyles';
import { trackICOInit, trackICOSubmit } from '../utils/analytics';
import { getCookieValue } from '@kmx/io-core/utils/cookie';
import { formatSeperatedInteger } from '../utils/format';
import { icoStart, initialFormSubmitted } from '../utils/offerEvents';
import { profileComplete } from '../utils/validation';
import ActiveStoreAppraisal from './ActiveStoreAppraisal/ActiveStoreAppraisal';
import ICOStepper from './ICOStepper';
import Ineligible from './Ineligible';
import IneligiblePhotoCapture from './IneligiblePhotoCapture';
import LicenseOrVin from './LicenseOrVin';
import LoadingScreen from './LoadingScreen';
import Offer from './Offer';
import OfferDisclaimer from './OfferDisclaimer';
import MuiOfferRefresh from './OfferRefresh/MuiOfferRefresh';
import { portalRoot } from './Portal/Portal';
import { AVAILABLE_DRIVES, AVAILABLE_TRANSMISSIONS } from '../utils/sku-helpers';
import { featureName } from '../types/testNames';

interface IICOProps {
    bypassRefreshOfferPrompt?: boolean;
    enabledFeatures: string[];
    offerId?: string;
    originPage?: string;
    renderPlateVinForm?: boolean;
    reset: (showLicenseVinForm: boolean) => void;
    startQuote?: IVinEligibility;
    storeId?: string;
    vehicleStyles?: IVinMatchingStylesEligibility;
}

const InstantCashOffer: React.FC<IICOProps> = props => {
    const appInsights = useAppInsightsContext();
    const effectHasRun = useRef(false);
    const { setCustomerInfo } = useCustomerInfo();
    const [icoStarted, setIcoStarted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [renderPlateVinForm, setRenderPlateVinForm] = useState(
        (!props.offerId || props.renderPlateVinForm !== undefined) && props.renderPlateVinForm !== false
    );
    const { start: startQuote } = useStartQuote(props);
    const { start: startDecodeVehicle } = useDecodeVehicle();
    const { startSSE: requestQuoteSSE } = useRequestOffer(props.originPage);
    const { trigger: vehicleIneligible } = useTriggerIneligibleEvent();
    const { vehicleInfo, setVehicleInfo, setVehicleConditionInfo, setFeatureInfo, resetVehicleInfo } = useVehicleInfo();
    const { formMetadata, plateDecodeError, zipDecodeError } = useFormContext();
    const { quoteId, offer, ineligible, isIneligible, storeAppraisal, clear } = useOfferContext();
    const hasValidOffer = offer?.valuation > 0;
    const { killswitch, isFeatureEnabled } = useFeatures();
    const [refreshOfferChanges, setRefreshOfferchanges] = useState<boolean | null>(
        props.bypassRefreshOfferPrompt !== undefined && props.bypassRefreshOfferPrompt ? true : null
    );
    const [previousOffer, setPreviousOffer] = useState<IQuote | null>(null);

    const isPicsyEligible = useCallback(
        (offer: IOffer) =>
            quoteId && offer?.isPicsyEligible && offer?.picsyUrl && isFeatureEnabled(featureName.PHOTO_CAPTURE),
        [quoteId, isFeatureEnabled]
    );

    const getIneligiblePageAnalyticsValue = useCallback(
        (offer: IOffer) => (isPicsyEligible(offer) ? 'icoineligible-picsyenabled' : 'icoineligible'),
        [isPicsyEligible]
    );

    const startIco = useCallback(
        async (vehicleData: IVehicleInfo) => {
            try {
                const pageType = getIneligiblePageAnalyticsValue(offer);
                const res = await startQuote({ vehicleData, pageType });

                if (res?.isSuccess) {
                    setIcoStarted(true);
                    trackICOInit(props.originPage, vehicleInfo.vin);
                    icoStart();
                } else if (res == null) {
                    return true;
                }
            } catch (err) {
                resetVehicleInfo();
                if (err?.response?.status != 401) {
                    console.log(err);
                }

                vehicleIneligible({
                    reason: CLIENT_INELIGIBLE_ERRORS.errorClientSide,
                    pageType: getIneligiblePageAnalyticsValue(offer),
                    isPicsyEligible: false, //should this be true?
                });
            }

            return false;
        },
        [vehicleIneligible, vehicleInfo, startQuote, getIneligiblePageAnalyticsValue, offer, props.originPage]
    );

    const decodeVehicleInfo = useCallback(
        async (startingMethod: IcoStartingMethod, setLoading = true) => {
            setIsLoading(true);

            const resp = await startDecodeVehicle({
                startingMethod,
                pageType: getIneligiblePageAnalyticsValue(offer),
            });

            let keepLoading = false;
            if (resp) {
                keepLoading = await startIco(resp);
            }

            if (setLoading && !keepLoading) {
                setIsLoading(false);
            }
        },
        [startIco, getIneligiblePageAnalyticsValue, startDecodeVehicle, offer]
    );

    const getStartedClick = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            initialFormSubmitted(vehicleInfo.vin, vehicleInfo.plate, vehicleInfo.state, vehicleInfo.zipcode);
            appInsights.trackEvent({
                name: 'GetStarted - clicked',
                properties: {
                    vin: vehicleInfo.vin || undefined,
                    plate: vehicleInfo.plate || undefined,
                },
            });

            await decodeVehicleInfo(formMetadata.startingMethod);
        },
        [decodeVehicleInfo, formMetadata]
    );

    const editVehicleClick = useCallback(async () => {
        clear();
        setIcoStarted(false);
        await decodeVehicleInfo('VIN');
    }, [decodeVehicleInfo, formMetadata]);

    const parseAnswersForPreviousOffer = (quote: IQuote) => {
        setPreviousOffer(quote);
        setCustomerInfo({
            email: quote.metaData.emailAddress,
        });
        setVehicleConditionInfo(prev => ({
            ...prev,
            mileage: formatSeperatedInteger(vehicleInfo.mileage ?? quote.mileage),
            conditionAnswers: quote.conditionQuestions,
        }));
        setVehicleInfo(prev => ({ ...prev, isComplete: true, vin: quote.vin, zipcode: quote.zipCode }));
        setFeatureInfo(prev => ({
            ...prev,
            isComplete: false,
            drive: AVAILABLE_DRIVES.find(x => x.code === quote.drive)?.value || null,
            transmission: AVAILABLE_TRANSMISSIONS.find(x => x.value === quote.transmission)?.value || null,
            availableOptions: quote.availableOptions,
            standardOptions: quote.standardOptions,
            style: { id: quote.styleCode, description: null },
        }));
    };

    useEffect(() => {
        if (effectHasRun.current) {
            return;
        }

        (async function initIco() {
            effectHasRun.current = true;
            if (profileComplete(vehicleInfo)) {
                await startIco(vehicleInfo);
            } else if (props.offerId) {
                appInsights.trackTrace(
                    { message: 'Offer refresh has been attempted' },
                    {
                        host: window.location.host,
                        originPage: props.originPage,
                    }
                );
                try {
                    const quote = await getQuoteV2(props.offerId, false, true);
                    if (quote.data && quote.data.offer) {
                        parseAnswersForPreviousOffer(quote.data.offer);
                    } else if (vehicleInfo.zipcode && vehicleInfo.vin) {
                        // old offer is older than 45 days, just start with VIN
                        await decodeVehicleInfo('VIN');
                    } else {
                        //the client didn't give us vin/zip, just reset
                        props.reset();
                    }
                } catch {
                    // Unable to get Quote. Try to decode vehicle
                    if (vehicleInfo.zipcode && vehicleInfo.vin) {
                        await decodeVehicleInfo('VIN');
                    } else {
                        props.reset();
                    }
                }
            } else if (vehicleInfo.zipcode && (vehicleInfo.vin || (vehicleInfo.plate && vehicleInfo.state))) {
                await decodeVehicleInfo(vehicleInfo.vin ? 'VIN' : 'LP');
            } else {
                setVehicleInfo(vehicleInfoInitialState);
            }
        })();
    }, [appInsights, decodeVehicleInfo, props.offerId, setVehicleInfo, startIco, vehicleInfo]);

    //Offer refresh question trigger -
    //if changes, decode the vehicle and do the normal process
    //if no changes, decode and start quote
    useEffect(() => {
        if (refreshOfferChanges === false) {
            setIsLoading(true);
            (async () => {
                const resp = await startDecodeVehicle({
                    startingMethod: 'VIN',
                    pageType: getIneligiblePageAnalyticsValue(offer),
                });

                await startQuote({
                    vehicleData: resp,
                    pageType: getIneligiblePageAnalyticsValue(offer),
                });
            })();
        } else if (refreshOfferChanges === true) {
            (async () => {
                await decodeVehicleInfo('VIN', refreshOfferChanges);
            })();
        }
    }, [refreshOfferChanges]);

    //offer refresh, no changes, and start quote completed (we have quoteId)
    useEffect(() => {
        if (refreshOfferChanges === false && quoteId) {
            (async () => {
                await trackICOSubmit(props.originPage, vehicleInfo.vin, quoteId);
                await requestQuoteSSE({ getIneligiblePageAnalyticsValue });
            })();
        }
    }, [refreshOfferChanges, quoteId]);

    //only for offer refresh no changes once the offer comes back (similar to the stepper)
    //But it's okay to not check the case because icoStarted will already be true for the other cases
    //and loading should be false if we have an offer back (or ineligible)
    useEffect(() => {
        if (offer || ineligible) {
            setIcoStarted(true);
            setIsLoading(false);
        }
    }, [offer, ineligible]);

    //this should already be false except for the offer refresh case with no changes
    useEffect(() => {
        if (offer || isIneligible) {
            setIsLoading(false);
        }
    }, [offer, isIneligible]);

    // Show plate/vin form if there was a plate or zip decode error
    useEffect(() => {
        if (plateDecodeError || zipDecodeError) {
            setRenderPlateVinForm(true);
        }
    }, [plateDecodeError, zipDecodeError]);

    useEffect(() => {
        if (window?.FS?.setUserVars) {
            window.FS.setUserVars({
                instantOfferVersion: 'v2',
            });
        }
    }, []);

    return (
        <div id="ico">
            {!killswitch && !icoStarted && (
                <>
                    {previousOffer && refreshOfferChanges === null && (
                        <>
                            <MuiOfferRefresh
                                setOtherChanges={setRefreshOfferchanges}
                                closeDialog={() => props.reset(false)}
                            />
                        </>
                    )}
                    {renderPlateVinForm && !ineligible && (
                        <div id="license-or-vin">
                            <LicenseOrVin onSubmit={getStartedClick} readOnly={isLoading} />
                        </div>
                    )}
                </>
            )}
            <div className="kmx-ico-component-root">
                {/* First loading screen */}
                {isLoading && <LoadingScreen />}

                {/* Offer/ineligible (above ICO stepper so stepper can be on the offer page)  */}
                <div id="offer-and-ineligible">
                    {hasValidOffer && (
                        <Offer
                            vin={vehicleInfo.vin}
                            originPage={props.originPage}
                            userId={getCookieValue('KmxMyKmx_0', 'userid')}
                            storeId={props.storeId}
                            onEditVehicleClick={editVehicleClick}
                        />
                    )}
                    {(isIneligible || killswitch) &&
                        (isPicsyEligible(offer) ? (
                            <IneligiblePhotoCapture />
                        ) : (
                            <Ineligible profile={vehicleInfo.profile} changeVehicle={props.reset} />
                        ))}
                    {/* Active store appraisal offer page */}
                    {storeAppraisal && (
                        <div>
                            <ActiveStoreAppraisal reset={props.reset} originPage={props.originPage} />
                        </div>
                    )}
                </div>

                {/* ICO stepper - vehicle profile, style/feature selection, condition info, etc. */}
                {!killswitch && icoStarted && !isIneligible && !storeAppraisal && (
                    <div id="ico-stepper">
                        <ICOStepper
                            originPage={props.originPage}
                            getIneligiblePageAnalyticsValue={getIneligiblePageAnalyticsValue}
                            previousOffer={previousOffer}
                            refreshOfferChanges={refreshOfferChanges}
                            onEditVehicleClick={editVehicleClick}
                        />
                    </div>
                )}

                {hasValidOffer && <OfferDisclaimer />}
                <div id={portalRoot}></div>
            </div>
        </div>
    );
};

export default InstantCashOffer;
